// Deliberately NOT a `declare global { interface Window { dataLayer } }`. Since NGF-6661 this module is
// compiled from source inside each consuming app's TypeScript program, where `Window.dataLayer` is
// already declared — `@cx-web/utils` types it as `DataLayerEvent[]`, non-optional. A second global
// declaration that disagrees on either the type or the `?` modifier is a hard error there (TS2717 /
// TS2687), so the window access is narrowed locally instead and the module stays agnostic about how
// its host types the global.
//
// STANDALONE, not `Window & { dataLayer?: unknown[] }`: an intersection ADDS to the host's declaration
// instead of replacing it, so in atlas's program the property would resolve to
// `DataLayerEvent[] & unknown[]` and required (a property is optional in an intersection only if it is
// optional in every constituent) — the `?? []` fallback below would be dead, and `push` would be typed
// against the host's `DataLayerEvent`, which is exactly the coupling this narrowing exists to avoid.
// Reaching it needs the `as unknown as` double cast. Same form as `getDefaultDataLayer` in ../index.tsx —
// keep the two in step.
type WindowWithDataLayer = { dataLayer?: unknown[] };

/**
 * Pushes a GA event onto a dataLayer: `{ event, ...eventData }`. Uses the provided dataLayer
 * (e.g. a host-supplied one) or falls back to `window.dataLayer`, which the consuming app's
 * GTM container listens on. The module never talks to GA directly.
 */
export function trackEvent(
  eventName: string,
  eventData: Record<string, unknown> = {},
  dataLayer: unknown[] | undefined,
) {
  const defaultDataLayer = () => {
    if (typeof window !== 'undefined') {
      const host = window as unknown as WindowWithDataLayer;
      host.dataLayer = host.dataLayer ?? [];
      return host.dataLayer;
    }
  };
  const activeDataLayer = dataLayer || defaultDataLayer();
  if (activeDataLayer) {
    activeDataLayer.push({
      event: eventName,
      ...eventData,
    });
  }
}
